/**
 * Theme (System / Light / Dark).
 *
 * The choice lives in localStorage under THEME_KEY and is applied as the `dark`
 * class on <html> (Tailwind `darkMode: 'class'`). index.html runs an equivalent
 * inline script before first paint; keep the two in sync.
 */

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_KEY = 'theme';

export const THEME_OPTIONS: { value: Theme; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | undefined {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined; // storage access can throw (privacy mode, sandboxed iframe)
  }
}

export function readTheme(storage: StorageLike | undefined = defaultStorage()): Theme {
  try {
    const value = storage?.getItem(THEME_KEY);
    return isTheme(value) ? value : 'system';
  } catch {
    return 'system';
  }
}

/** `system` is stored as "no key" so a fresh browser and an explicit System behave the same. */
export function writeTheme(theme: Theme, storage: StorageLike | undefined = defaultStorage()): void {
  try {
    if (theme === 'system') storage?.removeItem(THEME_KEY);
    else storage?.setItem(THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

export function systemPrefersDark(
  win: Pick<Window, 'matchMedia'> | undefined = typeof window !== 'undefined' ? window : undefined,
): boolean {
  return typeof win?.matchMedia === 'function' && win.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(theme: Theme, prefersDark: boolean = systemPrefersDark()): ResolvedTheme {
  if (theme === 'system') return prefersDark ? 'dark' : 'light';
  return theme;
}

/** Apply the theme to the document root and return what was applied. */
export function applyTheme(
  theme: Theme,
  root: HTMLElement = document.documentElement,
  prefersDark: boolean = systemPrefersDark(),
): ResolvedTheme {
  const resolved = resolveTheme(theme, prefersDark);
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  return resolved;
}
